import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  Field,
  ImageField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Image from 'next/image';

import * as icons from '@gn-audio/icons';
import { JabraIcon, mapSharedPropertiesToFields, useBreakpoint } from '@gn-audio/jss-components';
import { ComponentProps } from '@gn-audio/jss-components/lib/component-props';

import { Eyebrow } from 'common/components/Eyebrow';
type ZoomKSPSpotProps = {
  fields: ZoomKSPSpot;
} & ComponentProps;

type ZoomKSPSpot = {
  eyebrowText: TextField;
  eyebrowTextShared: TextField;
  headline: TextField;
  headlineShared: TextField;
  description: TextField;
  descriptionShared: TextField;
  image: ImageField;
  imageShared: ImageField;
  icon: Field<keyof typeof icons>;
  iconShared: Field<keyof typeof icons>;
  minAngle: TextField;
  minAngleShared: TextField;
  maxAngle: TextField;
  maxAngleShared: TextField;
};

gsap.registerPlugin(useGSAP, ScrollTrigger);

const ZoomKSPSpot = ({ fields }: ZoomKSPSpotProps) => {
  const fovContainerRef = useRef<HTMLDivElement>(null);
  const fovIndicatorRef = useRef<HTMLDivElement>(null);
  const degreesRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { isBelowXs } = useBreakpoint('xs', true);
  const { eyebrowText, headline, description, image, icon, minAngle, maxAngle } =
    mapSharedPropertiesToFields<ZoomKSPSpot>(fields);
  const isImageEmpty = !image?.value || Object.keys(image.value).length === 0;

  useGSAP(() => {
    if (imageRef.current && degreesRef.current && fovIndicatorRef.current) {
      const fovValue = { var: (minAngle?.value as number) || 72 };

      const updateFovNumber = () => {
        if (degreesRef.current) {
          degreesRef.current.innerHTML = fovValue.var.toFixed();
        }
      };

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: fovContainerRef.current,
          start: 'top center',
          end: '+=30%',
          scrub: 1,
        },
      });

      tl.fromTo(
        imageRef.current,
        { scale: 3, xPercent: 2, yPercent: 30 },
        { scale: 1, yPercent: 0, xPercent: 0 }
      );

      tl.to(
        fovValue,
        {
          var: (maxAngle?.value as number) || 180,
          ease: 'none',
          onUpdate: updateFovNumber,
        },
        '<'
      );
      tl.fromTo(fovIndicatorRef.current, { rotate: -45 }, { rotate: 135, ease: 'none' }, '<');
    }
  }, [fovIndicatorRef.current]);

  return (
    <>
      <div className="container">
        <div className="align-items-center justify-content-center flex flex-col gap-6">
          <Eyebrow text={eyebrowText.value as string} align="center" />
          <h2
            className="heading-2 py-2 text-center"
            dangerouslySetInnerHTML={{ __html: headline.value as string }}
          />
        </div>
      </div>
      {!isImageEmpty && (
        <div ref={fovContainerRef} className="relative aspect-[3840/1070] w-full overflow-hidden">
          {!isBelowXs && (
            <>
              <div
                ref={fovIndicatorRef}
                className="absolute left-1/2 top-full z-[2] aspect-[1/1] h-auto w-[200px] -translate-x-1/2 -translate-y-1/2 transform rounded-full border-4 border-solid border-b-yellow border-l-yellow border-r-transparent border-t-transparent"
              ></div>
              <div className="absolute left-1/2 top-[calc(100%-4rem)] z-[2] -translate-x-1/2 transform text-2xl text-[2rem] font-bold text-white [text-shadow:_1px_1px_2px_rgba(0,0,0,0.3)]">
                <span ref={degreesRef}></span>
                <span>°</span>
              </div>
            </>
          )}
          <Image
            ref={imageRef}
            src={image.value?.src as string}
            alt={image.value?.alt as string}
            width={image.value?.width as number}
            height={image.value?.height as number}
          />
        </div>
      )}

      <div className="container flex max-w-6xl flex-col items-center justify-center pt-10">
        {icon?.value && (
          <div className="flex h-20 w-20 self-center rounded-full bg-yellow">
            <JabraIcon Icon={icons?.[icon.value]} className="m-auto h-12 w-12" />
          </div>
        )}
        <p
          className="pt-8 text-center text-2xl font-normal sm:w-full sm:text-xl"
          dangerouslySetInnerHTML={{ __html: description.value as string }}
        />
      </div>
    </>
  );
};

export default withDatasourceCheck()(ZoomKSPSpot);
